import {FileUpload, Loader, Modal} from '@/components/atoms'
import {convertImageToWebP} from '@/helpers/shadcn/utils'
import {useImagesUpload} from '@/hooks/useImagesUpload'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {Constants, FILE} from '@/utils/constants/commonConstants'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useCallback, useEffect, useRef, useState} from 'react'

const UploadMediaModal = ({
  openUpload,
  setOpenUpload,
  entityType = 'product',
  title = 'Upload Media',
  productType = null,
  ...props
}) => {
  const {uploading, setUploadErrors, onUploadImages, uploadErrors, progress, setIsSingleImgUpload, setAllProdIds} =
    useImagesUpload()
  const [images, setImages] = useState([])
  const [reqArray, setReqArray] = useState([])
  const [imagesArray, setImagesArray] = useState([])
  const [oldImagesArray, setOldImagesArray] = useState([])
  const [convertedImgs, setConvertedImages] = useState([])
  const dispatch = useAppDispatch()
  const uploadErrorsRef = useRef([])

  const [isLoading, setLoader] = useState(false)
  const {
    imageCountAllowed = 50,
    maxCollectionOfImageSize = '25MB',
    maxIndividualImageSize = '2MB',
  } = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}
  const isCloseModal = () => {
    setOpenUpload(false)
    handleDeleteAll()
    setUploadErrors([])
    setReqArray([])
    setImagesArray([])
    setConvertedImages([])
    setLoader(false)
    setIsSingleImgUpload(false)
    setAllProdIds([])
    setOldImagesArray([])
  }
  const handleDelete = fileIndex => {
    const maxAllowedSize = Number(maxCollectionOfImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES

    const updateArray = setter => {
      setter(prev => {
        const updated = fileIndex === -1 ? prev.slice(1) : prev.filter((_, index) => index !== fileIndex)

        // If this is the oldImagesArray, calculate size here

        if (setter === setOldImagesArray) {
          const totalSize = updated.reduce((sum, file) => {
            const size = file.size ?? file.originalSize ?? 0
            return sum + size
          }, 0)
          // if (totalSize < maxAllowedSize) {
          if (individualImageValidation(updated)) {
            if (imageValidation(updated)) {
              setUploadErrors([])
            }
          }
          // }
        }

        return updated
      })
    }

    updateArray(setImages)
    updateArray(setImagesArray)
    updateArray(setReqArray)
    updateArray(setOldImagesArray)
    updateArray(setConvertedImages)
  }
  const handleDeleteAll = () => {
    setImages([])
  }
  // const filteredData = images?.filter(item => {
  //   return (
  //     (item?.type !== 'image/png' && item?.type !== 'image/jpeg' && item?.type !== 'image/webp') || item?.error == true
  //   )
  // })
  // useEffect(() => {
  //   if (filteredData?.length > 0) return
  //   setUploadErrors([])
  // }, [filteredData?.length, setUploadErrors])
  const onFilesAdded = useCallback(
    async event => {
      const allFiles = Array.from(event.target?.files || event.dataTransfer?.files || [])

      const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp'])
      const allowedImagesFormat = allFiles.filter(file => allowedTypes.has(file.type))
      if (allowedImagesFormat?.length === 0) {
        return
      }
      const selectedFileNames = new Set(images.map(image => image.name))
      const fileNames = []
      const newImages = allowedImagesFormat.filter(file => !selectedFileNames.has(file.name))
      if (newImages?.length === 0) {
        setUploadErrors(uploadErrorsRef.current)
        return
      }
      setOldImagesArray(prev => {
        const updatedArray = [...prev, ...newImages]
        imageValidation(updatedArray)
        individualImageValidation(updatedArray)

        return updatedArray
      })
      // for (const file of oldImagesArray || []) {
      //   file.error = false
      //   if (file?.size > Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES) {
      //     fileNames.push(file.name)

      //     file.error = true
      //     const errorConst = fileNames?.length > 1 ? 'these files are' : ' this file is'
      //     const err =
      //       imagesArray?.length === 1
      //         ? 'Each image must be less than or equal to 2 MB.'
      //         : `${fileNames} ${errorConst} exceed the 2 MB individual file size limit.`
      //     setUploadErrors([err])
      //   }
      // }
      setLoader(true)
      const convertedFiles = await Promise.all(
        newImages?.map(file => convertImageToWebP(file, {entityType: entityType}))
      )
      setConvertedFiles(convertedFiles)
    },
    [images, setUploadErrors]
  )
  const individualImageValidation = updatedArray => {
    if (!Array.isArray(updatedArray) || updatedArray.length === 0) return true

    const fileNames = []
    let hasError = false

    const maxSizeBytes = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES

    for (const file of updatedArray) {
      file.error = false

      if (file?.size > maxSizeBytes) {
        file.error = true
        fileNames.push(file.name)
        hasError = true
      }
    }

    if (hasError) {
      const errorConst = fileNames.length > 1 ? 'these files are' : 'this file is'
      const errMsg =
        updatedArray.length === 1
          ? 'Each image must be less than or equal to 2 MB.'
          : `${fileNames.join(', ')} ${errorConst} exceeding the 2 MB individual file size limit.`

      setUploadErrors([errMsg])
      return false
    }

    return true
  }

  const imageValidation = updatedArray => {
    if (!Array.isArray(updatedArray) || updatedArray.length === 0) return true

    const maxSizeInMB = Number(maxCollectionOfImageSize?.match(/\d+/)?.[0])
    const maxAllowedSize = maxSizeInMB * FILE.SIZE_BYTES
    const totalSize = updatedArray.reduce((sum, file) => sum + (file?.size || 0), 0)
    if (updatedArray?.length > imageCountAllowed) {
      setUploadErrors([`You can not upload more than ${imageCountAllowed} files. Remove some files to continue.`])
      return false
    }
    if (totalSize >= maxAllowedSize) {
      setUploadErrors([`Total image size must not exceed ${maxSizeInMB} MB.`])
      return false
    }

    return true
  }
  const setConvertedFiles = convertedFiles => {
    if (Array.isArray(convertedFiles) && convertedFiles.length > 0) {
      const webpFiles = []
      const originalFiles = []
      const metadataList = []
      const convertedImgs = []

      for (const {webpFile, file, metadata} of convertedFiles) {
        webpFiles.push(webpFile)
        originalFiles.push(file)
        metadataList.push(metadata)
        convertedImgs.push({metadata, webpFile})
      }

      setLoader(false)
      setImagesArray(prev => [...prev, ...webpFiles])
      setImages(prev => [...prev, ...originalFiles])
      setReqArray(prev => [...prev, ...metadataList])
      setConvertedImages(prev => [...prev, ...convertedImgs])
    }
  }
  const handleUpload = async () => {
    setUploadErrors([])
    setAllProdIds([])
    setIsSingleImgUpload(false)
    if (!convertedImgs?.length) return
    const uploadPromises = convertedImgs.map(({webpFile, metadata}) => {
      const formData = new FormData()
      formData.append('images', webpFile)
      const modifiedMetadata = {...metadata, referenceEntity: entityType}
      formData.append('metadata', JSON.stringify({mediaUploadMetadata: [modifiedMetadata]}))

      return new Promise((resolve, reject) => {
        onUploadImages(formData, response => {
          if (response?.status === 'SUCCESS') {
            resolve({status: 'fullfilled', response})
          } else {
            reject({status: 'rejected', response})
          }
        })
      })
    })

    try {
      const results = await Promise.allSettled(uploadPromises)
      if (uploadErrors?.length === 0) {
        isCloseModal()
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Images uploaded successfully',
            type: 'success',
          })
        )
      }

      // const failed = results.filter(result => result.status === 'rejected')
      // const succeeded = results.filter(result => result.status === 'fulfilled')

      // if (failed.length > 0) {
      //   console.warn(`${failed.length} image(s) failed to upload.`)
      //   // Optionally toast or UI notification
      // }

      // isCloseModal() // Close once all done
    } catch (error) {
      console.error('Unexpected error during uploads:', error)
    }
  }

  // const handleUpload = () => {
  //   for (let i = 0; i < convertedImgs?.length; i++) {
  //     let formData = new FormData()

  //     formData.append('images', convertedImgs[i]?.webpFile)
  //     const metadataPayload = JSON.stringify({
  //       mediaUploadMetadata: [convertedImgs[i]?.metadata],
  //     })
  //     formData.append('metadata', metadataPayload)
  //     const res = onUploadImages(formData, () => {
  //       isCloseModal()
  //     })
  //   }
  //   // formData.append('metadata', JSON.stringify({mediaUploadMetadata: reqArray}))
  // }
  // const handleUpload = async () => {
  //   if (!convertedImgs?.length) return

  //   const uploadPromises = convertedImgs?.map(({webpFile, metadata}) => {
  //     console.log('Uploading file:', webpFile, 'with metadata:', metadata)
  //     const formData = new FormData()
  //     formData.append('images', webpFile)
  //     const metadataPayload = JSON.stringify({
  //       mediaUploadMetadata: [metadata],
  //     })
  //     formData.append('metadata', metadataPayload)
  //     return onUploadImages(formData)
  //   })

  //   try {
  //     const results = await uploadPromises
  //     console.log('Upload successful:000000000', uploadPromises)
  //     isCloseModal()
  //   } catch (error) {
  //     console.error('Upload failed00998776554344:', error)
  //     // Optionally handle per-file errors inside onUploadImages
  //   }
  // }
  const files = images.map(file => file.name)
  const hasErrors = uploadErrors?.length > 0
  const renderErrors = () => {
    if (hasErrors) {
      return (
        <div>
          {uploadErrors.map(error => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )
    }
    return null
  }

  const imageUri = images?.length === 1 && !hasErrors ? URL?.createObjectURL(images[0]) : ''
  useEffect(() => {
    uploadErrorsRef.current = uploadErrors
  }, [uploadErrors])
  return (
    <Modal
      isDark={true}
      onCloseModal={isCloseModal}
      onClickSecondaryButton={isCloseModal}
      onClickPrimaryButton={handleUpload}
      showModal={openUpload}
      className="upload-target-modal"
      title={title}
      primaryButtonText={'Done'}
      primaryButtonDisabled={hasErrors || images?.length <= 0 || images.length > imageCountAllowed ? true : false}
      type={MODAL_TYPE.SMALL}
      showSecondaryButton={true}
      secondaryButtonProps={{
        disabled: uploading,
      }}
      secondaryButtonText={Constants.Cancel}
      data-testId="upload-images-modal"
      showTertiaryButton={false}
      onClickTertiaryButton={isCloseModal}
      tertiaryButtonText="Cancel"
      content={
        isLoading ? (
          <Loader />
        ) : (
          <FileUpload
            acceptedExtensions=".jpg,.png,.webp"
            description="Drag and drop your file here (only .jpg, .png, .webp)"
            label={`Upload media files (The maximum combined size of all uploaded images must not exceed ${maxCollectionOfImageSize}.)`}
            variant={uploading ? Constants.Uploading : Constants.Edit}
            buttonLabel={Constants.UploadFile}
            onFilesAdded={onFilesAdded}
            onUploadDelete={handleDelete}
            type="image"
            required={true}
            files={files}
            // className={images.length > 0 && hasErrors ? 'disabled' : ''}
            // containerClassName={images.length > 0 ? 'disabled' : ''}
            multiple={true}
            value={files}
            isDark={true}
            feedbackType={hasErrors ? Constants.Error : Constants.Info}
            helperText={renderErrors()}
            onUploadError={error => {
              setUploadErrors([error])
            }}
            progressPercentage={progress}
            imageUri={imageUri}
            dataTestId="upload-images"
            {...props}
          />
        )
      }
    />
  )
}

export default UploadMediaModal
